import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import OnboardingLayout from "@/components/OnboardingLayout";
import { useBusiness } from "@/context/BusinessContext";
import { api, apiError } from "@/lib/api";
import { Input, Textarea, Field } from "@/components/ui";

function TagInput({ value, onChange, placeholder, testId }) {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div>
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} data-testid={testId}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2.5">
          {value.map((t) => (
            <span key={t} className="tag" onClick={() => onChange(value.filter((x) => x !== t))} style={{ cursor: "pointer" }}>
              {t} ✕
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OnboardingBusiness() {
  const nav = useNavigate();
  const { active, loading, createBusiness, load, deleteBusiness } = useBusiness();
  const [wsName, setWsName] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState(null);

  React.useEffect(() => {
    if (active) {
      const p = active.profile || {};
      setForm({
        name: p.name || active.name || "",
        category: p.category || "",
        description: p.description || "",
        services: p.services || [],
        serviceAreas: p.serviceAreas || [],
        businessHours: p.businessHours || "",
        timezone: p.timezone || "UTC",
        languages: p.languages || ["en"],
        defaultTone: p.defaultTone || "friendly, concise, direct",
        defaultReplyLength: p.defaultReplyLength || "short",
        handoffFallback: p.handoffFallback || "Let me connect you with a team member.",
        unknownAnswerFallback: p.unknownAnswerFallback || "I'm not certain about that — a team member will confirm.",
        safeDefault: p.safeDefault || "I've noted this and a team member will follow up.",
      });
    }
  }, [active]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    setCreating(true);
    try { await createBusiness(wsName); toast.success("Workspace created"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/businesses/${active.id}/profile`, form);
      await load();
      toast.success("Business profile saved");
      nav("/onboarding/knowledge");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeWorkspace = async () => {
    if (!window.confirm(`Delete "${active.name}" and everything in it? This cannot be undone.`)) return;
    try {
      await deleteBusiness(active.id);
      toast.success("Workspace deleted");
      nav("/app");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!active) {
    if (loading) {
      return <OnboardingLayout current="business" title="Create your workspace" />;
    }
    return (
      <OnboardingLayout current="business" title="Create your workspace"
        primaryAction={{ label: "Create workspace", onClick: create, loading: creating, disabled: !wsName.trim(), testId: "create-workspace-submit", icon: <ArrowRight size={16} /> }}>
        <form onSubmit={(e) => { e.preventDefault(); create(); }} data-testid="create-workspace-form" className="w-full max-w-md">
          <Field label="Business name" htmlFor="ws">
            <Input id="ws" value={wsName} onChange={(e) => setWsName(e.target.value)} placeholder="BrightNest Home Cleaning" data-testid="workspace-name" required />
          </Field>
        </form>
      </OnboardingLayout>
    );
  }

  if (!form) return null;

  const previewTone = form.defaultTone || "friendly";

  return (
    <OnboardingLayout current="business" title="Business profile"
      secondaryAction={{ label: "Delete workspace", onClick: removeWorkspace, variant: "danger", testId: "delete-workspace" }}
      primaryAction={{ label: "Save & continue", onClick: save, loading: saving, testId: "profile-save", icon: <ArrowRight size={16} /> }}>
      <form onSubmit={(e) => { e.preventDefault(); save(); }} data-testid="business-profile-form" className="w-full space-y-10">
        <div className="space-y-5">
          <Field label="Business name" htmlFor="name"><Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" required /></Field>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Category" htmlFor="cat"><Input id="cat" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Residential cleaning" data-testid="profile-category" /></Field>
            <Field label="Timezone" htmlFor="tz"><Input id="tz" value={form.timezone} onChange={(e) => set("timezone", e.target.value)} placeholder="America/Los_Angeles" data-testid="profile-timezone" /></Field>
          </div>
          <Field label="Short description" htmlFor="desc"><Textarea id="desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="profile-description" /></Field>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Services offered"><TagInput value={form.services} onChange={(v) => set("services", v)} placeholder="Add a service + Enter" testId="profile-services" /></Field>
            <Field label="Service areas"><TagInput value={form.serviceAreas} onChange={(v) => set("serviceAreas", v)} placeholder="Add a city + Enter" testId="profile-areas" /></Field>
          </div>
          <Field label="Business hours" htmlFor="hrs"><Input id="hrs" value={form.businessHours} onChange={(e) => set("businessHours", e.target.value)} placeholder="Mon–Sat, 8 AM–6 PM" data-testid="profile-hours" /></Field>
        </div>

        <div className="space-y-5 pt-8" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="heading-eyebrow">Tone & fallbacks</div>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Default tone" htmlFor="tone"><Input id="tone" value={form.defaultTone} onChange={(e) => set("defaultTone", e.target.value)} data-testid="profile-tone" /></Field>
            <Field label="Default reply length" htmlFor="len">
              <select id="len" className="input" value={form.defaultReplyLength} onChange={(e) => set("defaultReplyLength", e.target.value)} data-testid="profile-replylength">
                <option value="short">short</option><option value="medium">medium</option><option value="long">long</option>
              </select>
            </Field>
          </div>
          <Field label="Handoff fallback message" htmlFor="hf"><Input id="hf" value={form.handoffFallback} onChange={(e) => set("handoffFallback", e.target.value)} data-testid="profile-handoff" /></Field>
          <Field label="Unknown-answer fallback" htmlFor="uf"><Input id="uf" value={form.unknownAnswerFallback} onChange={(e) => set("unknownAnswerFallback", e.target.value)} data-testid="profile-unknown" /></Field>
          <Field label="Safe default (no action possible)" htmlFor="sd"><Input id="sd" value={form.safeDefault} onChange={(e) => set("safeDefault", e.target.value)} data-testid="profile-safe" /></Field>
        </div>

        <div className="pl-4" style={{ borderLeft: "2px solid var(--accent-2)" }}>
          <div className="heading-eyebrow mb-1.5">Voice preview</div>
          <p className="text-sm" style={{ color: "var(--text-2)" }}>
            "Hi! Thanks for reaching out. I'll keep this <strong style={{ color: "var(--text)" }}>{form.defaultReplyLength}</strong> and <strong style={{ color: "var(--text)" }}>{previewTone}</strong>. How can I help today?"
          </p>
        </div>
      </form>
    </OnboardingLayout>
  );
}
